import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Menu } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { PRODUCT_VARIATION_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveProductVariation from "@/hooks/requests/product-variations/use-remove-product-variation";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import IVariation from "@/interfaces/variation.interface";
import { VariationsTableMessages } from "@/messages/variation/variations-table.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: VariationsTableMessages;
  selected: IVariation;
}

export function VariationPopover({ isOpen, anchorEl, onClose, messages, selected }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { isVisible, handleOpen, handleClose } = useDialog<IVariation>();
  const { remove, isLoading } = useRemoveProductVariation();
  const { mutate } = useMutate();
  const _id = selected.id;

  const hasNoPermissionToEdit = useHasNoPermission(PRODUCT_VARIATION_PERMISSIONS.EDIT);
  const hasNoPermissionToDelete = useHasNoPermission(PRODUCT_VARIATION_PERMISSIONS.DELETE);

  const onSuccess = () => {
    mutate(SWR_KEYS.getProductVariations);
    handleClose();
  };

  const confirmDelete = async () => {
    const successMessage = messages.deleteProductVariationSuccess;

    await remove({ _id, onSuccess, successMessage });
  };

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <NotificationItem
        icon={<EditIcon />}
        disabled={hasNoPermissionToEdit}
        onClick={() => router(ROUTE_URLS.updateProductVariation(lang, selected.id))}
        title={messages.updatePopover}
      />

      <NotificationItem
        icon={<DeleteIcon />}
        disabled={hasNoPermissionToDelete}
        onClick={() => handleOpen(selected)}
        title={messages.removePopover}
      />

      <ConfirmationDialog
        isOpen={isVisible}
        onClose={handleClose}
        onConfirm={() => confirmDelete()}
        titleText={messages.deleteProductVariationDialogTitle}
        descriptionText={messages.deleteProductVariationDialogContent}
        cancelText={messages.deleteProductVariationDialogCancel}
        isLoading={isLoading}
        confirmText={messages.deleteProductVariationDialogConfirm}
      />
    </Menu>
  );
}
